import { canonicalPersonalNumberKey } from "../../../utils/personnelDisplay";

/** Sentinel for מחסן (available warehouse stock). */
export const INVENTORY_UNIT_FILTER_STORAGE = "__mahsan__";

/** Resolve unit id for an assignee key; keys in `assignments.first` may not match map keys exactly. */
export function getUnitIdForAssigneeKey(personnelUnitMap, assigneeKey) {
  if (!personnelUnitMap || assigneeKey == null) return undefined;
  const raw = String(assigneeKey).trim();
  if (!raw) return undefined;
  const direct =
    personnelUnitMap.get(raw) ??
    personnelUnitMap.get(canonicalPersonalNumberKey(assigneeKey));
  if (direct) return direct;
  const target = canonicalPersonalNumberKey(assigneeKey);
  for (const [k, v] of personnelUnitMap.entries()) {
    if (canonicalPersonalNumberKey(k) === target) return v;
  }
  return undefined;
}

/** True if this child has any `assignments.first` slice keyed to them (מ.א. format–tolerant). */
export function childHasFirstAllocation(item, childPersonalNumber) {
  if (!childPersonalNumber || !item?.assignments?.first) return false;
  const target = canonicalPersonalNumberKey(childPersonalNumber);
  return Object.keys(item.assignments.first).some(
    (k) => canonicalPersonalNumberKey(k) === target,
  );
}

/**
 * Child inventory row belongs to their unit: any first assignee is in that unit,
 * or unassigned row is at that unit (`currentUnitId`). If `childUnitId` is missing,
 * fall back to rows where the child has a `first` slice (legacy narrow view).
 */
export function itemBelongsToChildUnit(
  item,
  childUnitId,
  personnelUnitMap,
  childPersonalNumberFallback,
) {
  if (!item) return false;
  if (!childUnitId) {
    if (!childPersonalNumberFallback) return false;
    return childHasFirstAllocation(item, childPersonalNumberFallback);
  }
  const first = item.assignments?.first || {};
  for (const pn of Object.keys(first)) {
    const uid = getUnitIdForAssigneeKey(personnelUnitMap, pn);
    if (uid && uid === childUnitId) return true;
  }
  if (item.currentUnitId && item.currentUnitId === childUnitId) return true;
  return false;
}

export function getParent9263FirestoreId(units) {
  const u = units?.find((unit) => String(unit.unitName).trim() === "9263");
  return u?.id ?? null;
}

/** Assignee's unit, else currentUnitId, else parent 9263 id. */
export function getItemEffectiveUnitIdForFilter(
  item,
  personnelUnitMap,
  parent9263FirestoreId,
) {
  const assigned =
    item.assignedPersonnel?.length > 0 &&
    (item.calculatedStatus === "assigned" || item.status === "assigned");
  if (assigned) {
    const uid = getUnitIdForAssigneeKey(
      personnelUnitMap,
      item.assignedPersonnel[0].personalNumber,
    );
    return uid ?? null;
  }
  if (item.currentUnitId) return item.currentUnitId;
  return parent9263FirestoreId ?? null;
}

export function itemMatchesMahsanFilter(item, parent9263FirestoreId) {
  if (item.calculatedStatus !== "available") return false;
  if ((item.assignedQuantity ?? 0) > 0) return false;
  const cu = item.currentUnitId;
  if (parent9263FirestoreId) return !cu || cu === parent9263FirestoreId;
  return !cu;
}

/**
 * OR across selected real units and optional מחסן.
 * @param {Set<string>} selectedSet — built once per filter ids change
 */
export function itemMatchesUnitFilterSelection(
  item,
  selectedSet,
  personnelUnitMap,
  parent9263FirestoreId,
) {
  if (!selectedSet?.size) return true;
  if (selectedSet.has(INVENTORY_UNIT_FILTER_STORAGE)) {
    if (itemMatchesMahsanFilter(item, parent9263FirestoreId)) return true;
  }
  const eff = getItemEffectiveUnitIdForFilter(
    item,
    personnelUnitMap,
    parent9263FirestoreId,
  );
  for (const id of selectedSet) {
    if (id === INVENTORY_UNIT_FILTER_STORAGE) continue;
    if (eff === id) return true;
  }
  return false;
}
